import { type Locale, cmsLocales, LOCALE_PATTERN } from "./i18n";

export const IS_PROD_DEPLOYMENT = process.env.NEXT_PUBLIC_SERVER_URL === "https://elevenlabs.io";

export type MissingLocaleBehavior = "redirect-to-default" | "show-not-found" | "no-index";

export interface TargetLocaleOptions {
  locale: Locale;
  enabled: boolean;
  missingLocaleBehavior: MissingLocaleBehavior;
}
interface DocLocaleParams {
  enabledLocales?: Locale[] | null | undefined;
  missingLocaleBehavior?: MissingLocaleBehavior | null | undefined;
}

export function getTargetLocaleOptions(doc: DocLocaleParams, locale: Locale): TargetLocaleOptions {
  if (!doc.enabledLocales || doc.enabledLocales.length === 0)
    return {
      locale,
      enabled: true,
      missingLocaleBehavior: "no-index",
    };

  return {
    locale,
    enabled: doc.enabledLocales.includes(locale),
    missingLocaleBehavior: doc.missingLocaleBehavior || "no-index",
  };
}

export type TranslationStats = Record<
  string,
  {
    localizableFieldCount: number;
    localizedFieldCount: number;
    missingLocalizationFields: string[];
    localizableTextLength: number;
    missingLocalizationTextLength: number;
  }
>;

export function getLargestMissingTranslation(translationStats: TranslationStats): number {
  const languages = Object.keys(translationStats);
  if (languages.length === 0) return 0;

  return Object.values(translationStats).reduce((max, stats) => {
    if (stats.localizableTextLength === 0) return max;
    const untranslatedPercentage = (stats.missingLocalizationTextLength / stats.localizableTextLength) * 100;
    return Math.max(max, untranslatedPercentage);
  }, 0);
}

/**
 * Check if the path has a locale prefix, eg. /en/hello-world -> true
 */
export function hasLocalePrefix(path: string): boolean {
  return cmsLocales.some(locale => isTranslatedPath(path, locale));
}

/**
 * Check if the path is a translated path for a given locale, eg. /fr/hello-world, fr -> true
 */
export function isTranslatedPath(path: string, locale: string) {
  return path.startsWith(`/${locale}/`) || path === `/${locale}`;
}
/**
 * Extract locale from pathname if present, eg. /en/hello-world -> en
 */

export function extractLocaleFromPath(pathname: string): { locale: Locale | null; pathWithoutLocale: string } {
  if (!pathname.startsWith("/")) {
    pathname = `/${pathname}`;
  }

  const hasLocale = cmsLocales.some(locale => isTranslatedPath(pathname, locale));
  if (!hasLocale) {
    return { locale: null, pathWithoutLocale: pathname };
  }

  const path = pathname.replace(/^\//, "");
  const match = path.match(LOCALE_PATTERN);
  if (!match || !match[1]) {
    return { locale: null, pathWithoutLocale: pathname };
  }

  let pathWithoutLocale = pathname.replace(`/${match[1]}`, "");
  if (!pathWithoutLocale.startsWith("/")) {
    pathWithoutLocale = `/${pathWithoutLocale}`;
  }

  return {
    locale: match[1] as Locale,
    pathWithoutLocale,
  };
}
